import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Heart, Loader2, ShoppingBag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Button } from "../../components/ui/button";
import { StockBadge, formatGBP } from "../../components/common/StockBadge";
import { useShop } from "../../context/ShopContext";
import api from "../../lib/api";

export default function Favourites() {
  const { addToBasket, refreshFavs } = useShop();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/me/favourites").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (pid) => {
    await api.delete(`/me/favourites/${pid}`);
    setItems((x) => x.filter((i) => i.product_id !== pid));
    refreshFavs();
  };

  return (
    <div className="space-y-6" data-testid="customer-favourites">
      <div>
        <h1 className="font-playfair text-2xl sm:text-3xl text-white">Favourites</h1>
        <p className="mt-2 text-muted-foreground">Products you've saved for quick reservation.</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>
      ) : items.length === 0 ? (
        <GlassCard className="p-12 flex flex-col items-center text-center" hover={false} data-testid="favourites-empty">
          <div className="grid place-items-center h-14 w-14 rounded-2xl bg-gold/10 border border-gold/20"><Heart className="h-7 w-7 text-gold" /></div>
          <p className="mt-5 font-playfair text-xl text-white">No favourites yet</p>
          <p className="mt-2 text-sm text-muted-foreground max-w-md">Tap the heart on any product to save it here.</p>
          <Button asChild className="cf-gold-btn font-semibold mt-6"><Link to="/fireworks">Browse Fireworks</Link></Button>
        </GlassCard>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <GlassCard key={p.product_id} className="p-4 flex gap-4" hover={false} data-testid={`fav-item-${p.product_id}`}>
              <Link to={`/product/${p.slug}`} className="h-20 w-20 rounded-lg overflow-hidden bg-black/30 shrink-0">
                {p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover" /> : <div className="h-full w-full grid place-items-center text-white/20"><ShoppingBag className="h-6 w-6" /></div>}
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${p.slug}`}><h3 className="font-playfair text-white leading-tight line-clamp-2 hover:text-gold">{p.name}</h3></Link>
                <div className="mt-1 flex items-center gap-2"><span className="text-gold font-semibold">{formatGBP(p.retail_price)}</span><StockBadge status={p.stock_status} /></div>
                <div className="mt-2 flex items-center gap-2">
                  <Button size="sm" disabled={p.stock_status !== "in_stock"} onClick={() => addToBasket({ ...p })} data-testid={`fav-add-basket-${p.product_id}`} className="cf-gold-btn font-semibold gap-1 h-8 text-xs"><ShoppingBag className="h-3.5 w-3.5" /> Add</Button>
                  <button onClick={() => remove(p.product_id)} data-testid={`fav-remove-${p.product_id}`} className="text-white/40 hover:text-firered"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
}
